/**
 * UploadRicevutaModal — F-008 Phase 6 (IMPL-P6-006).
 *
 * Modale top-level che orchestra il flusso completo di upload ricevuta:
 *  1. PDF dropzone (presign + PUT XHR + sha256)
 *  2. Form RHF con resolver Zod (`commitRicevutaInputSchema`):
 *       - protocolloAde (regex `RLI/MM/YYYY/NNNNNN`)
 *       - dataRegistrazione (date input, non futura — server-side)
 *       - firmaDigitaleStatus (radio VALID / UNRECOGNIZED / SKIPPED)
 *       - firmaDigitaleNote (textarea condizionale, ≥10 char per UNRECOGNIZED)
 *       - "Procedo comunque" checkbox (richiesta per UNRECOGNIZED)
 *  3. CompletionConfirmationDialog di conferma esplicita
 *  4. `useUploadRicevuta.mutate(commitInput)` — su 200 chiama `onCompleted`
 *
 * Pattern di riferimento:
 *  - `SegnalazioneErroreModal` (F-007) — overlay HTML + ESC + click-outside
 *  - PHASE_6_PLAN.md IMPL-P6-006 (specifiche complete)
 *  - INTRODUCTION.md Pattern 7 (Zod schema condivisa) per il resolver
 *
 * Accessibilità WCAG 2.1 AA:
 *  - 1.3.1 Info & Relationships: form fieldset / legend per radio group
 *  - 2.1.2 No Keyboard Trap: ESC chiude (anche con focus dentro form)
 *  - 2.4.3 Focus Order: focus iniziale al titolo, ordine naturale in tab
 *  - 2.4.7 Focus Visible: ereditato dal design system (focus-visible:ring)
 *  - 3.3.1 Error Identification: errori inline + role="alert"
 *  - 3.3.3 Error Suggestion: hint format protocollo, hint min-chars textarea
 *  - 3.3.4 Error Prevention: dialog di conferma annidato prima del commit
 *  - 4.1.2 Name, Role, Value: aria-label su progressbar, aria-disabled su submit
 *  - 4.1.3 Status Messages: role="alert" per 409/422/500, role="status" per progress
 *
 * Reduced motion (`prefers-reduced-motion: reduce`):
 *  - skip animate-pulse sullo skeleton del PDFReceiptPreview (gestito al suo interno)
 *  - skip transizione progress bar (custom — vedi `progressClass` sotto)
 */
import * as React from 'react';
import { useForm, FormProvider, Controller } from 'react-hook-form';
import {
  commitRicevutaInputSchema,
  type CommitRicevutaInput,
  type FirmaDigitaleStatus,
} from '@app/shared';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { useUploadRicevuta, mapUploadRicevutaError } from '../hooks/useUploadRicevuta';
import { PDFReceiptDropzone } from './PDFReceiptDropzone';
import { PDFReceiptPreview } from './PDFReceiptPreview';
import { ProtocolloInput } from './ProtocolloInput';
import { CompletionConfirmationDialog } from './CompletionConfirmationDialog';
import { XMLDropzone } from './XMLDropzone';

export interface UploadRicevutaModalProps {
  open: boolean;
  praticaId: string;
  /** Etichetta cliente per il riepilogo nella dialog di conferma. */
  clientName?: string;
  /** Chiamato dopo il commit andato a buon fine (200 OK). */
  onCompleted: () => void;
  /** Chiamato quando l'utente chiude la modale (ESC, click outside, Annulla). */
  onClose: () => void;
}

/* -------------------------------------------------------------------------- */
/* Form values type                                                           */
/* -------------------------------------------------------------------------- */

/**
 * Shape del form RHF — copre i campi compilati dall'utente. I campi tecnici
 * (`s3Key`, `sha256`, `sizeBytes`, `praticaId`) sono iniettati al submit dal
 * caller, non controllati dall'input.
 */
interface RicevutaFormValues {
  protocolloAde: string;
  dataRegistrazione: string;
  firmaDigitaleStatus: FirmaDigitaleStatus;
  firmaDigitaleNote: string;
  procedoComunque: boolean;
}

/* -------------------------------------------------------------------------- */
/* Reduced motion hook (locale a UploadRicevutaModal)                         */
/* -------------------------------------------------------------------------- */

function usePrefersReducedMotion(): boolean {
  const [prefers, setPrefers] = React.useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  });
  React.useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = (e: MediaQueryListEvent) => setPrefers(e.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);
  return prefers;
}

/* -------------------------------------------------------------------------- */
/* Component                                                                  */
/* -------------------------------------------------------------------------- */

export function UploadRicevutaModal({
  open,
  praticaId,
  clientName,
  onCompleted,
  onClose,
}: UploadRicevutaModalProps) {
  const prefersReducedMotion = usePrefersReducedMotion();
  const titleRef = React.useRef<HTMLHeadingElement | null>(null);

  const [pdfInfo, setPdfInfo] = React.useState<{
    s3Key: string;
    sha256: string;
    sizeBytes: number;
  } | null>(null);
  const [uploadProgress, setUploadProgress] = React.useState<number>(0);
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [stateError, setStateError] = React.useState<string | null>(null);
  const [storageError, setStorageError] = React.useState(false);
  const [genericError, setGenericError] = React.useState<string | null>(null);
  const [dropzoneError, setDropzoneError] = React.useState<string | null>(null);

  const form = useForm<RicevutaFormValues>({
    // Risolvere il commit schema completo richiede campi tecnici (s3Key,
    // sha256, sizeBytes, praticaId) che NON sono nel form. Costruiamo il
    // payload finale al submit e lasciamo a RHF la sola validazione "user
    // input" via i singoli register/Controller. Il commit schema viene
    // comunque applicato server-side (defense-in-depth).
    mode: 'onBlur',
    defaultValues: {
      protocolloAde: '',
      dataRegistrazione: '',
      firmaDigitaleStatus: 'VALID' as FirmaDigitaleStatus,
      firmaDigitaleNote: '',
      procedoComunque: false,
    },
  });

  const mutation = useUploadRicevuta(praticaId);

  // Reset all state when modal opens (no carry-over between pratiche).
  React.useEffect(() => {
    if (open) {
      setPdfInfo(null);
      setUploadProgress(0);
      setConfirmOpen(false);
      setStateError(null);
      setStorageError(false);
      setGenericError(null);
      setDropzoneError(null);
      form.reset({
        protocolloAde: '',
        dataRegistrazione: '',
        firmaDigitaleStatus: 'VALID',
        firmaDigitaleNote: '',
        procedoComunque: false,
      });
      mutation.reset();
      // Focus iniziale al titolo per screen reader announce.
      const t = window.setTimeout(() => {
        titleRef.current?.focus();
      }, 0);
      return () => window.clearTimeout(t);
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const firmaStatus = form.watch('firmaDigitaleStatus');
  const firmaNote = form.watch('firmaDigitaleNote');
  const procedoComunque = form.watch('procedoComunque');

  // canSubmit = pdf caricato + form valido + (se UNRECOGNIZED) note ≥10 + checkbox
  const isUnrecognized = firmaStatus === 'UNRECOGNIZED';
  const motivazioneOk = !isUnrecognized || firmaNote.trim().length >= 10;
  const checkboxOk = !isUnrecognized || procedoComunque;
  const canSubmit =
    Boolean(pdfInfo) &&
    form.formState.isValid !== false && // RHF reports false only after first onSubmit
    motivazioneOk &&
    checkboxOk &&
    !mutation.isPending;

  /**
   * Estrae uno short ID dalla pratica per la dialog di conferma. cuid è
   * lessicograficamente ordinato; gli ultimi 8 char sono sufficientemente
   * distinguibili lato UI.
   */
  const praticaShortId = React.useMemo(
    () => praticaId.slice(-8).toUpperCase(),
    [praticaId],
  );

  /**
   * Validazione manuale al click di "Conferma e completa" — RHF `handleSubmit`
   * passa `data` solo se i validatori passano. Apriamo la dialog solo se:
   *  - pdfInfo presente (uploader ha completato la PUT)
   *  - tutti i campi RHF validi
   *  - condizioni UNRECOGNIZED soddisfatte
   */
  const onValidSubmit = (values: RicevutaFormValues) => {
    if (!pdfInfo) return;
    if (isUnrecognized && !motivazioneOk) return;
    if (isUnrecognized && !checkboxOk) return;
    // Cleanup banner errori vecchi prima di aprire la dialog.
    setStateError(null);
    setStorageError(false);
    setGenericError(null);
    setConfirmOpen(true);
    // ref-only: i values sono già nello state RHF, non serve stash qui.
    void values;
  };

  /**
   * Handler di conferma definitiva (dalla CompletionConfirmationDialog).
   * Costruisce il payload completo e chiama la mutation.
   */
  const handleConfirm = async () => {
    if (!pdfInfo) return;
    const values = form.getValues();
    const input: CommitRicevutaInput = {
      praticaId,
      s3Key: pdfInfo.s3Key,
      sha256: pdfInfo.sha256,
      sizeBytes: pdfInfo.sizeBytes,
      protocolloAde: values.protocolloAde,
      dataRegistrazione: values.dataRegistrazione,
      firmaDigitaleStatus: values.firmaDigitaleStatus,
      firmaDigitaleNote: values.firmaDigitaleNote
        ? values.firmaDigitaleNote.trim()
        : undefined,
    };

    // Defense-in-depth: re-validate via shared schema before sending. Server
    // also valida, ma evitiamo round-trip se il client può catturare l'errore.
    const parsed = commitRicevutaInputSchema.safeParse(input);
    if (!parsed.success) {
      setGenericError('Dati non validi: verificare i campi del modulo.');
      setConfirmOpen(false);
      return;
    }

    mutation.mutate(parsed.data, {
      onSuccess: () => {
        setConfirmOpen(false);
        onCompleted();
      },
      onError: (err) => {
        setConfirmOpen(false);
        const mapped = mapUploadRicevutaError(err);
        if (mapped.type === 'duplicate') {
          form.setError('protocolloAde', {
            type: 'server',
            message:
              mapped.detail ||
              'Numero di protocollo già utilizzato. Verificare il valore.',
          });
        } else if (mapped.type === 'stale') {
          setStateError(
            `La pratica è in stato "${mapped.currentStato}". Aggiornare la vista e riprovare.`,
          );
        } else if (mapped.type === 'storage') {
          setStorageError(true);
        } else {
          setGenericError(mapped.message);
        }
      },
    });
  };

  if (!open) return null;

  // Progress bar: skip transition quando reduced motion preferito.
  const progressClass = prefersReducedMotion
    ? 'h-2 rounded-full bg-primary'
    : 'h-2 rounded-full bg-primary transition-all';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 overflow-y-auto"
      onClick={(e) => {
        if (e.target === e.currentTarget && !mutation.isPending) onClose();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape' && !mutation.isPending && !confirmOpen) {
          onClose();
        }
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="upload-ricevuta-title"
        aria-describedby="upload-ricevuta-desc"
        className="my-8 w-full max-w-3xl rounded-lg border bg-background p-6 shadow-lg"
      >
        <header className="mb-4">
          <h2
            ref={titleRef}
            id="upload-ricevuta-title"
            tabIndex={-1}
            className="text-lg font-semibold tracking-tight focus:outline-none"
          >
            Carica ricevuta di registrazione
          </h2>
          <p
            id="upload-ricevuta-desc"
            className="mt-1 text-sm text-muted-foreground"
          >
            Carica il PDF firmato dall'AdE, inserisci protocollo e data
            registrazione, poi conferma il completamento della pratica.
          </p>
        </header>

        {/* Banner errori asincroni (top of modal) */}
        {stateError && (
          <div
            role="alert"
            className="mb-4 rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive"
          >
            {stateError}
          </div>
        )}
        {storageError && (
          <div
            role="alert"
            className="mb-4 rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive"
          >
            Errore di archiviazione (S3). Verificare la connessione e
            riprovare. Il file caricato potrebbe non essere stato salvato.
          </div>
        )}
        {genericError && (
          <div
            role="alert"
            className="mb-4 rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive"
          >
            {genericError}
          </div>
        )}
        {dropzoneError && (
          <div
            role="alert"
            className="mb-4 rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive"
          >
            {dropzoneError}
          </div>
        )}

        <FormProvider {...form}>
          <form
            onSubmit={form.handleSubmit(onValidSubmit)}
            className="space-y-4"
            noValidate
          >
            <div className="grid gap-6 md:grid-cols-2">
              {/* Colonna sx: dropzone + preview + xml opzionale */}
              <div className="space-y-3">
                <PDFReceiptDropzone
                  praticaId={praticaId}
                  onFileReady={(info) => {
                    setPdfInfo(info);
                    setUploadProgress(100);
                    setDropzoneError(null);
                  }}
                  onError={(msg) => {
                    setPdfInfo(null);
                    setUploadProgress(0);
                    setDropzoneError(msg);
                  }}
                />
                {/* Progress bar accessibile (visibile solo in flight). Il
                    PDFDropzone interno mostra già un proprio progressbar; qui
                    teniamo un fallback role="progressbar" sul wrapper modale
                    per esporre lo stato all'AT secondo PHASE_6_PLAN.md. */}
                {uploadProgress > 0 && uploadProgress < 100 && (
                  <div
                    role="progressbar"
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={uploadProgress}
                    aria-label="Progresso upload ricevuta"
                    className="h-2 w-full overflow-hidden rounded-full bg-muted"
                  >
                    <div
                      className={progressClass}
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                )}
                <PDFReceiptPreview
                  praticaId={praticaId}
                  s3Key={pdfInfo?.s3Key ?? null}
                />
                <XMLDropzone disabled={mutation.isPending} />
              </div>

              {/* Colonna dx: campi form */}
              <div className="space-y-4">
                <ProtocolloInput disabled={mutation.isPending} />

                <div className="space-y-1">
                  <Label htmlFor="dataRegistrazione">
                    Data registrazione AdE
                  </Label>
                  <Input
                    id="dataRegistrazione"
                    type="date"
                    aria-required="true"
                    aria-invalid={Boolean(
                      form.formState.errors.dataRegistrazione,
                    )}
                    aria-describedby={
                      form.formState.errors.dataRegistrazione
                        ? 'dataRegistrazione-error'
                        : undefined
                    }
                    disabled={mutation.isPending}
                    {...form.register('dataRegistrazione', {
                      required: 'Data registrazione obbligatoria',
                    })}
                  />
                  {form.formState.errors.dataRegistrazione && (
                    <p
                      id="dataRegistrazione-error"
                      role="alert"
                      className="text-xs text-destructive"
                    >
                      {String(form.formState.errors.dataRegistrazione.message)}
                    </p>
                  )}
                </div>

                {/* Firma digitale radio group */}
                <fieldset className="space-y-2">
                  <legend className="text-sm font-medium">
                    Firma digitale ricevuta
                  </legend>
                  <Controller
                    name="firmaDigitaleStatus"
                    control={form.control}
                    render={({ field }) => (
                      <div
                        role="radiogroup"
                        aria-label="Stato firma digitale ricevuta"
                        className="space-y-1"
                      >
                        <label className="flex items-center gap-2 text-sm">
                          <input
                            type="radio"
                            name="firmaDigitaleStatus"
                            value="VALID"
                            checked={field.value === 'VALID'}
                            onChange={() => field.onChange('VALID')}
                            disabled={mutation.isPending}
                          />
                          <span>Riconosciuta</span>
                        </label>
                        <label className="flex items-center gap-2 text-sm">
                          <input
                            type="radio"
                            name="firmaDigitaleStatus"
                            value="UNRECOGNIZED"
                            checked={field.value === 'UNRECOGNIZED'}
                            onChange={() => field.onChange('UNRECOGNIZED')}
                            disabled={mutation.isPending}
                          />
                          <span>Non riconosciuta</span>
                        </label>
                        <label className="flex items-center gap-2 text-sm">
                          <input
                            type="radio"
                            name="firmaDigitaleStatus"
                            value="SKIPPED"
                            checked={field.value === 'SKIPPED'}
                            onChange={() => field.onChange('SKIPPED')}
                            disabled={mutation.isPending}
                          />
                          <span>Non verificata</span>
                        </label>
                      </div>
                    )}
                  />
                </fieldset>

                {/* Conditional UNRECOGNIZED block */}
                {isUnrecognized && (
                  <div className="space-y-2 rounded-md border border-amber-300/60 bg-amber-50/50 p-3">
                    <div className="space-y-1">
                      <Label htmlFor="firmaDigitaleNote">
                        Motivazione (obbligatoria, min 10 caratteri)
                      </Label>
                      <textarea
                        id="firmaDigitaleNote"
                        rows={3}
                        maxLength={500}
                        aria-required="true"
                        aria-describedby="firmaDigitaleNote-hint"
                        disabled={mutation.isPending}
                        className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:opacity-50"
                        {...form.register('firmaDigitaleNote')}
                      />
                      <p
                        id="firmaDigitaleNote-hint"
                        className="text-xs text-muted-foreground"
                      >
                        Spiega brevemente perché la firma non è stata
                        riconosciuta (min 10 caratteri).
                      </p>
                    </div>
                    <label className="flex items-start gap-2 text-sm">
                      <input
                        type="checkbox"
                        disabled={mutation.isPending}
                        {...form.register('procedoComunque')}
                        className="mt-0.5"
                      />
                      <span>
                        Procedo comunque con il completamento, accettando il
                        rischio segnalato.
                      </span>
                    </label>
                  </div>
                )}
              </div>
            </div>

            <footer className="flex items-center justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={mutation.isPending}
              >
                Annulla
              </Button>
              <Button
                type="submit"
                aria-disabled={!canSubmit}
                disabled={!canSubmit || mutation.isPending}
              >
                {mutation.isPending
                  ? 'Completamento in corso…'
                  : 'Conferma e completa'}
              </Button>
            </footer>
          </form>
        </FormProvider>
      </div>

      {/* Nested confirmation dialog — z-[60] sopra la modale principale (z-50). */}
      <CompletionConfirmationDialog
        open={confirmOpen}
        praticaShortId={praticaShortId}
        clientName={clientName}
        protocolloAde={form.getValues('protocolloAde')}
        dataRegistrazione={form.getValues('dataRegistrazione')}
        firmaDigitaleStatus={form.getValues('firmaDigitaleStatus')}
        isPending={mutation.isPending}
        onConfirm={handleConfirm}
        onCancel={() => setConfirmOpen(false)}
      />
    </div>
  );
}
